import React, { useState, useEffect } from 'react'
import { Alert, Button, Col, Form, Row, Table } from 'react-bootstrap'
import claimGroupRolesService from '@rd-web-markets/shared/dist/services/claim_groups/claimGroupRoles.service'
import { handleError } from '@rd-web-markets/shared/dist/store/features/alertSlice'
import { CancelButton, DeleteButton } from '@rd-web-markets/shared/dist/util/buttons'
import { useDispatch } from 'react-redux'
import './TeamStep.css'

const CONSULTANT_ROLES = [
  { value: 'claim_group_access_lead_consultant', label: 'Lead Consultant' },
  { value: 'claim_group_access_suporting_consultant', label: 'Supporting Consultant' }
]

export default function TeamStep({ claimGroup, managers, admins, consultants, onChange, onNext, onBack }) {
  const [newConsultantId, setNewConsultantId] = useState('')
  const [newConsultantRole, setNewConsultantRole] = useState('')
  const [validationError, setValidationError] = useState(null)
  const dispatch = useDispatch();

  const isEdit = !!claimGroup.id
  const managerOptions = [...(managers || []), ...(admins || [])]
  const consultantsAttributes = claimGroup.consultants_attributes || []

  const onManagerChange = (e) => {
    onChange({ target: { name: 'admin_id', value: e.target.value } })
  }

  const allPeopleOptions = [...(managers || []), ...(admins || []), ...(consultants || [])]
  .filter((person, index, arr) => arr.findIndex(p => String(p.id) === String(person.id)) === index)

  const addConsultant = async () => {
    if (!newConsultantId || !newConsultantRole) return

    const alreadyAdded = consultantsAttributes.find(c => String(c.id) === String(newConsultantId))
    if (alreadyAdded) return

    const consultantUser = [...(consultants || []), ...(admins || []), ...(managers || [])]
      .find(c => String(c.id) === String(newConsultantId))
    if (!consultantUser) return

    if (isEdit) {
      try {
        const updatedClaimGroup = await claimGroupRolesService.create(
          claimGroup.id,
          consultantUser.id,
          newConsultantRole
        )
        onChange({
          target: {
            name: 'consultants_attributes',
            value: updatedClaimGroup.consultant_roles
          }
        })
      } catch (error) {
        dispatch(handleError(error))
        return
      }
    } else {
      const updated = [
        ...consultantsAttributes,
        { id: consultantUser.id, name: consultantUser.name, role: newConsultantRole }
      ]
      onChange({ target: { name: 'consultants_attributes', value: updated } })
    }

    setNewConsultantId('')
    setNewConsultantRole('')
    setValidationError(null)
  }

  const removeConsultant = async (consultant) => {
    if (isEdit) {
      try {
        const updatedClaimGroup = await claimGroupRolesService.delete(
          claimGroup.id,
          consultant.user?.id || consultant.id,
          consultant.role
        )
        onChange({
          target: {
            name: 'consultants_attributes',
            value: updatedClaimGroup.consultant_roles
          }
        })
      } catch (error) {
        dispatch(handleError(error))
      }
    } else {
      const updated = consultantsAttributes.filter(c => String(c.id) !== String(consultant.id))
      onChange({ target: { name: 'consultants_attributes', value: updated } })
    }
  }

  const getInitials = (name) => {
    if (!name) return '?'
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  const getRoleLabel = (roleValue) => {
    return CONSULTANT_ROLES.find(r => r.value === roleValue)?.label || roleValue
  }

  const validate = () => {
    if (!claimGroup.admin_id) {
      setValidationError('A manager must be selected')
      return false
    }
    const hasLead = consultantsAttributes.some(c => c.role === 'claim_group_access_lead_consultant')
    if (!hasLead) {
      setValidationError('At least one Lead Consultant must be added')
      return false
    }
    setValidationError(null)
    return true
  }

  const continueIfValid = () => {
    if (validate()) onNext()
  }

  useEffect(() => {
    if (validationError) {
      const hasManager = !!claimGroup.admin_id
      const hasLead = consultantsAttributes.some(c => c.role === 'claim_group_access_lead_consultant')
      if (hasManager && hasLead) setValidationError(null)
    }
  }, [claimGroup.admin_id, consultantsAttributes, validationError])

  return (
    <div className="team-step">
      <h5 className="team-step__title">Team</h5>

      {validationError && (
        <Alert variant="danger" className="team-step__alert">
          {validationError}
        </Alert>
      )}

      <Form.Group as={Row} className="mb-4">
        <Form.Label column md={4} className="team-step__label">
          Manager <span className="team-step__required">*</span>
        </Form.Label>
        <Col md={8}>
          <Form.Control
            as="select"
            value={claimGroup.admin_id || ''}
            onChange={onManagerChange}
            className={!claimGroup.admin_id && validationError ? 'team-step__input--invalid' : ''}
          >
            <option value=''>Select manager...</option>
            {managerOptions?.map(m => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </Form.Control>
        </Col>
      </Form.Group>

      <div className="mb-3">
        <Form.Label className="team-step__label">
          Consultants <span className="team-step__required">*</span>
        </Form.Label>

        {consultantsAttributes.length > 0 && (
          <Table bordered size="sm" className="mb-3">
            <thead>
              <tr>
                <th className="team-step__col-narrow"></th>
                <th>Name</th>
                <th>Role</th>
                <th className="team-step__col-narrow"></th>
              </tr>
            </thead>
            <tbody>
              {consultantsAttributes.map(c => (
                <tr key={c.id}>
                  <td className="team-step__avatar-cell">
                    <div className="team-step__avatar">{getInitials(c.name)}</div>
                  </td>
                  <td className="team-step__name-cell">{c.name}</td>
                  <td>{getRoleLabel(c.role)}</td>
                  <td>
                    <DeleteButton onClick={() => removeConsultant(c)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        <Row className="g-2 align-items-end">
          <Col md={5}>
            <Form.Control
              as="select"
              value={newConsultantId}
              onChange={e => setNewConsultantId(e.target.value)}
            >
              <option value=''>Select consultant...</option>
              {allPeopleOptions
                .filter(c => !consultantsAttributes.find(a => String(a.id) === String(c.id)))
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                .map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
            </Form.Control>
          </Col>
          <Col md={4}>
            <Form.Control
              as="select"
              value={newConsultantRole}
              onChange={e => setNewConsultantRole(e.target.value)}
            >
              <option value=''>Select role...</option>
              {CONSULTANT_ROLES.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </Form.Control>
          </Col>
          <Col md={3}>
            <Button
              variant="outline-primary"
              onClick={addConsultant}
              disabled={!newConsultantId || !newConsultantRole}
              className="team-step__add-btn"
            >
              + Add
            </Button>
          </Col>
        </Row>
      </div>

      <div className="team-step__footer">
        {onBack && (
          <Button onClick={onBack} variant='primary' className="team-step__back-btn">← Back</Button>
        )}
        <Button variant="primary" onClick={continueIfValid} className="team-step__next-btn">
          Continue →
        </Button>
      </div>
    </div>
  )
}
